import { useMemo } from 'react';
import { Trash2, ChevronRight } from 'lucide-react';
import { useDraftsList, useDeleteDraft } from '@/features/contracts/hooks/useContractDrafts';
import { formatPrice, formatFullName } from '@/features/contracts/utils/contractFormatter';
import type { ContractDraft } from '@/features/contracts/types/contract.types';
import { DataList } from '@/Components/shared/List';
import { useListState, useListSearch } from '@/hooks/list';
import { paginationToSkipTake } from '@/utils/list.utils';
import type { ColumnDef } from '@/types/list.types';
import { FormInput } from '@/Components/shared/FormInput';
import { useConfirm } from '@/hooks/useConfirm';
import { useCurrentUserWording } from '@/hooks/useVendorWording';
import { useAuthStore } from '@/Stores/auth-v2-store';

const STEP_LABELS_BASE: Record<number, string> = {
  1: 'Bénéficiaire',
  2: 'Véhicule',
};

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffH < 24) return `Il y a ${diffH}h`;
  if (diffD === 1) return 'Hier';
  if (diffD < 7) return `Il y a ${diffD} jours`;

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export interface ContractDraftsTabProps {
  onResume: (draft: ContractDraft) => void;
}

export function ContractDraftsTab({ onResume }: ContractDraftsTabProps) {
  const { currentUser } = useAuthStore();
  const isInternal = currentUser?.type === 'INTERNAL';

  const wording = useCurrentUserWording();
  const STEP_LABELS: Record<number, string> = useMemo(() => ({
    ...STEP_LABELS_BASE,
    3: wording.singular,
    4: 'Paiement',
  }), [wording]);

  const { data: drafts, isLoading } = useDraftsList();
  const deleteDraft = useDeleteDraft();
  const { confirm, ConfirmDialogComponent } = useConfirm();

  const { searchTerm, debouncedSearch, setSearchTerm } = useListSearch({ debounceMs: 300 });

  const { pagination, setPagination } = useListState({ defaultLimit: 20 });

  const filteredDrafts = useMemo(() => {
    if (!drafts) return [];
    if (!debouncedSearch) return drafts;
    const term = debouncedSearch.toLowerCase();
    return drafts.filter((d) => {
      const label = d.label?.toLowerCase() ?? '';
      const society = d.societyName?.toLowerCase() ?? '';
      const creator = d.creatorName?.toLowerCase() ?? '';
      return label.includes(term) || society.includes(term) || creator.includes(term);
    });
  }, [drafts, debouncedSearch]);

  const paginatedDrafts = useMemo(() => {
    const { skip, take } = paginationToSkipTake(pagination.page, pagination.limit);
    return filteredDrafts.slice(skip, skip + take);
  }, [filteredDrafts, pagination]);

  const columns: ColumnDef<ContractDraft>[] = useMemo(() => {
    const cols: ColumnDef<ContractDraft>[] = [];

    if (isInternal) {
      cols.push({
        key: 'society',
        label: 'Société',
        render: (draft) => (
          <div>
            <div className="tw-text-sm tw-font-medium">{draft.societyName || '-'}</div>
            {draft.creatorName && (
              <div className="tw-text-xs tw-text-gray-500">{draft.creatorName}</div>
            )}
          </div>
        ),
      });
    }

    cols.push(
      {
        key: 'beneficiary',
        label: 'Bénéficiaire',
        render: (draft) => {
          const beneficiary = draft.formData?.beneficiary;
          return (
            <div>
              <div className="tw-text-sm tw-font-medium">
                {beneficiary?.lastName
                  ? formatFullName(beneficiary.firstName, beneficiary.lastName)
                  : '-'}
              </div>
              {beneficiary?.email && (
                <div className="tw-text-xs tw-text-gray-500">{beneficiary.email}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'vehicle',
        label: 'Véhicule',
        render: (draft) => {
          if (draft.currentStep < 2) return <span className="tw-text-sm tw-text-gray-400">-</span>;
          const vehicle = draft.formData?.vehicle;
          const vehicleInfo = [vehicle?.brand, vehicle?.model].filter(Boolean).join(' ');

          return (
            <div>
              <div className="tw-text-sm tw-font-medium">{vehicle?.registration || '-'}</div>
              {vehicleInfo && (
                <div className="tw-text-xs tw-text-gray-500">{vehicleInfo}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'warranty',
        label: wording.singular,
        render: (draft) => {
          const formData = draft.formData || {};
          if (!formData.warrantyName) return <span className="tw-text-sm tw-text-gray-400">-</span>;

          const monthlyAmount = formData.monthlyPriceWithFees || formData.monthlyPrice;
          const price = formData.paymentType === 2 && monthlyAmount
            ? `${formatPrice(monthlyAmount)} /mois`
            : formData.sellingPrice
              ? formatPrice(formData.sellingPrice)
              : null;

          return (
            <div>
              <div className="tw-text-sm tw-font-medium">{formData.warrantyName}</div>
              {price && (
                <div className="tw-text-xs tw-text-green-600 tw-font-medium">{price}</div>
              )}
            </div>
          );
        },
      },
      {
        key: 'currentStep',
        label: 'Étape',
        render: (draft) => {
          const totalSteps = draft.currentStep >= 4 ? 4 : 3;
          return (
            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-flex tw-gap-0.5">
                {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
                  <div
                    key={step}
                    className={`tw-w-5 tw-h-1.5 tw-rounded-full ${
                      step <= draft.currentStep
                        ? 'tw-bg-affinicar-primary'
                        : 'tw-bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="tw-text-xs tw-text-gray-500">
                {draft.currentStep}/{totalSteps} — {STEP_LABELS[draft.currentStep] || '?'}
              </span>
            </div>
          );
        },
      },
      {
        key: 'updatedAt',
        label: 'Dernière modification',
        render: (draft) => (
          <span className="tw-text-sm tw-text-gray-500">
            {formatRelativeDate(draft.updatedAt)}
          </span>
        ),
      },
    );

    return cols;
  }, [isInternal, wording.singular, STEP_LABELS]);

  return (
    <>
      {ConfirmDialogComponent}
      <DataList
        embedded
        data={paginatedDrafts}
        total={filteredDrafts.length}
        isLoading={isLoading}
        columns={columns}
        filterSearch={
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
            <FormInput
              type="text"
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Rechercher un brouillon..."
              size="sm"
            />
          </div>
        }
        pagination={pagination}
        onPaginationChange={setPagination}
        rowActions={[
          {
            label: 'Supprimer',
            icon: Trash2,
            onClick: async (draft) => {
              const confirmed = await confirm({
                title: 'Supprimer le brouillon',
                message: 'Êtes-vous sûr de vouloir supprimer ce brouillon ? Cette action est irréversible.',
                confirmLabel: 'Supprimer',
                cancelLabel: 'Annuler',
                variant: 'danger',
              });
              if (confirmed) deleteDraft.mutate(draft.uuid);
            },
            variant: 'danger',
            permission: 'contract.create',
          },
          {
            label: 'Reprendre',
            icon: ChevronRight,
            onClick: (draft) => onResume(draft),
            variant: 'secondary',
            permission: 'contract.create',
          },
        ]}
        onRowClick={(draft) => onResume(draft)}
        getRowKey={(draft) => draft.uuid}
        emptyMessage="Aucun brouillon en cours"
      />
    </>
  );
}
